import * as React from 'react';

import { translate } from '~l10n/client';
import {
  TimerAnchor,
  createTimerAnchor,
  createTimerAnchorFromStart,
  getRemainingSeconds,
} from '~shared/utils/ntpTimer';

import { IconTimer } from '../../../src/shared/components/icons/large/IconTimer/IconTimer';
import { formatTime } from '../../quiz/utils/formatTime';

const activity = require('activity');

/**
 * QuizTimer uses NTP-style offset calculation to eliminate clock skew between client and server.
 * Uses the shared ntpTimer utility for consistent timer calculations across the application.
 *
 * Required activity properties (set by API response handlers):
 *
 * 1. student_timer_started: boolean
 *    - Set to true when timer starts, false when paused/stopped
 *
 * 2. student_active_timer: { expires_at: string, server_time: string } | null
 *    - Set from the active_timer field in API responses (GET /activities/room/:room_name, POST /start)
 *    - expires_at: ISO timestamp when timer expires
 *    - server_time: ISO timestamp of server's current time
 *
 * 3. student_timer_network_latency: number (optional)
 *    - Round-trip network latency in milliseconds
 *    - Calculate as: (t4 - t1) where t1 = Date.now() before request, t4 = Date.now() after response
 *    - Used to correct for one-way network delay (halved internally)
 *    - Defaults to 0 if not provided
 *
 * Example API response handler:
 * ```
 * const t1 = Date.now();
 * const response = await fetchActivity(activityId);
 * const t4 = Date.now();
 *
 * if (response.active_timer) {
 *   activity.set('student_timer_started', true);
 *   activity.set('student_active_timer', response.active_timer);
 *   activity.set('student_timer_network_latency', t4 - t1);
 * }
 * ```
 */

interface ActiveTimer {
  expires_at: string;
  server_time: string;
}

interface QuizTimerProps {
  quizTimerSeconds?: number;
  onExpired?: () => void;
  status?: 'active' | 'paused';
}

type TimerState = 'not-started' | 'active' | 'warning' | 'critical' | 'expired';

const TIMER_WARNING_THRESHOLD = 0.25;
const TIMER_CRITICAL_THRESHOLD = 0.1;

const formatAriaLabel = (totalSeconds: number): string => {
  if (totalSeconds === 0) {
    return translate('Time expired');
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0 && seconds > 0) {
    const minuteText =
      minutes === 1 ? translate('minute') : translate('minutes');
    const secondText =
      seconds === 1 ? translate('second') : translate('seconds');
    return `${minutes} ${minuteText} and ${seconds} ${secondText} remaining`;
  } else if (minutes > 0) {
    const minuteText =
      minutes === 1 ? translate('minute') : translate('minutes');
    return `${minutes} ${minuteText} remaining`;
  } else {
    const secondText =
      seconds === 1 ? translate('second') : translate('seconds');
    return `${seconds} ${secondText} remaining`;
  }
};

const getTimerState = (
  remainingSeconds: number,
  totalSeconds: number,
  isPaused: boolean
): TimerState => {
  if (isPaused) {
    return 'not-started';
  }
  if (remainingSeconds === 0) {
    return 'expired';
  } else if (
    remainingSeconds < Math.min(totalSeconds * TIMER_CRITICAL_THRESHOLD, 60)
  ) {
    return 'critical';
  } else if (remainingSeconds < totalSeconds * TIMER_WARNING_THRESHOLD) {
    return 'warning';
  }
  return 'active';
};

export const QuizTimer: React.FC<QuizTimerProps> = ({
  quizTimerSeconds,
  onExpired,
  status = 'active',
}) => {
  const timerAnchorRef = React.useRef<TimerAnchor | null>(null);
  const [remainingSeconds, setRemainingSeconds] = React.useState<number>(() => {
    return quizTimerSeconds || 0;
  });
  const [announcement, setAnnouncement] = React.useState<string>('');
  const previousRemainingRef = React.useRef<number | null>(null);
  const [triggerUpdate, setTriggerUpdate] = React.useState(0);
  const isMountedRef = React.useRef(false);
  const hasInitialized = React.useRef(false);

  React.useEffect(() => {
    isMountedRef.current = true;

    const handleActivityChange = () => {
      setTriggerUpdate((prev) => prev + 1);
    };

    activity.on('change:student_timer_started', handleActivityChange);
    activity.on('change:student_active_timer', handleActivityChange);
    activity.on('change:student_timer_started_at', handleActivityChange);

    // Check again after a short delay in case the API call completes after mount
    const timeoutId = setTimeout(() => {
      const studentTimerStarted =
        activity.has('student_timer_started') &&
        activity.get('student_timer_started');
      const studentTimerStartedAt = activity.has('student_timer_started_at')
        ? activity.get('student_timer_started_at')
        : null;

      if (
        (studentTimerStarted || studentTimerStartedAt) &&
        !hasInitialized.current
      ) {
        setTriggerUpdate((prev) => prev + 1);
      }
    }, 100);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
      activity.off('change:student_timer_started', handleActivityChange);
      activity.off('change:student_active_timer', handleActivityChange);
      activity.off('change:student_timer_started_at', handleActivityChange);
    };
  }, []);

  // useEffect is appropriate here - timer initialization doesn't require synchronous DOM updates
  // and doesn't need to block the browser paint cycle
  React.useEffect(() => {
    if (!quizTimerSeconds) {
      timerAnchorRef.current = null;
      setRemainingSeconds(0);
      return;
    }

    // Helper to start the timer interval - extracted to avoid duplication
    const startTimerInterval = () => {
      const intervalId = setInterval(() => {
        const newRemaining = getRemainingSeconds(timerAnchorRef.current);
        setRemainingSeconds(newRemaining);

        if (newRemaining <= 0) {
          clearInterval(intervalId);
        }
      }, 1000);

      return () => {
        clearInterval(intervalId);
      };
    };

    try {
      const studentTimerStarted =
        activity.has('student_timer_started') &&
        activity.get('student_timer_started');

      if (!studentTimerStarted) {
        timerAnchorRef.current = null;
        setRemainingSeconds(quizTimerSeconds);
        hasInitialized.current = true;
        return;
      }

      const activeTimer: ActiveTimer | null = activity.has(
        'student_active_timer'
      )
        ? activity.get('student_active_timer')
        : null;

      if (!activeTimer) {
        const startedAt = activity.has('student_timer_started_at')
          ? activity.get('student_timer_started_at')
          : null;

        if (startedAt) {
          const anchor = createTimerAnchorFromStart({
            startedAt,
            durationSeconds: quizTimerSeconds,
          });
          const initialRemaining = getRemainingSeconds(anchor);

          timerAnchorRef.current = anchor;
          setRemainingSeconds(initialRemaining);
          setAnnouncement('');
          previousRemainingRef.current = null;
          hasInitialized.current = true;

          return startTimerInterval();
        }

        timerAnchorRef.current = null;
        setRemainingSeconds(quizTimerSeconds);
        hasInitialized.current = true;
        return;
      }

      const networkLatencyMs = activity.has('student_timer_network_latency')
        ? activity.get('student_timer_network_latency')
        : 0;

      // Use the stored receive time from when the timer data was originally fetched.
      // Without this, remounting the component (e.g. after closing the feedback modal)
      // would re-anchor stale server data to Date.now(), resetting the timer display.
      const receiveTime: number | null = activity.has(
        'student_timer_receive_time'
      )
        ? activity.get('student_timer_receive_time')
        : null;

      if (receiveTime === null) {
        console.error(
          'QuizTimer: student_timer_receive_time missing — timer may reset incorrectly on remount'
        );
      }

      const anchor = createTimerAnchor({
        expiresAt: activeTimer.expires_at,
        serverTime: activeTimer.server_time,
        networkLatencyMs,
        anchorTime: receiveTime !== null ? receiveTime : undefined,
      });

      const initialRemaining = getRemainingSeconds(anchor);

      timerAnchorRef.current = anchor;
      setRemainingSeconds(initialRemaining);
      setAnnouncement('');
      previousRemainingRef.current = null;
      hasInitialized.current = true;

      return startTimerInterval();
    } catch (error) {
      console.error('QuizTimer error:', error);
      timerAnchorRef.current = null;
      setRemainingSeconds(quizTimerSeconds);
      hasInitialized.current = true;
    }
  }, [quizTimerSeconds, triggerUpdate]);

  React.useEffect(() => {
    const previousRemaining = previousRemainingRef.current;

    if (
      remainingSeconds === 0 &&
      previousRemaining !== null &&
      previousRemaining > 0
    ) {
      setAnnouncement('Time expired');
      onExpired?.();
    } else if (remainingSeconds > 0 && announcement) {
      setAnnouncement('');
    }

    previousRemainingRef.current = remainingSeconds;
  }, [remainingSeconds, announcement, onExpired]);

  const formattedTime = formatTime(remainingSeconds);
  const ariaLabel = formatAriaLabel(remainingSeconds);

  if (!quizTimerSeconds) {
    return null;
  }

  const timerState = getTimerState(
    remainingSeconds,
    quizTimerSeconds,
    status === 'paused'
  );

  return (
    <>
      <div
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>
      <div className="quiz-timer" aria-label={ariaLabel}>
        <IconTimer state={timerState} status={status} />
        <span className="quiz-timer__time" aria-hidden="true">
          {formattedTime}
        </span>
      </div>
    </>
  );
};
